/* Trends — every machine against every day, then each stage as a line. */

import { Blueprint, PanelHead } from '../../components/ui'
import { HeatLegend, MachineHeat } from '../../components/charts/MachineHeat'
import { MachineDayLines } from '../../components/charts/MachineDayLines'
import { num } from '../../lib/format'
import { useIsDark } from '../../lib/theme'
import { dayNum, stageColor, stageDash } from './parts'
import type { Ctx } from './parts'

export function TrendsTab({ c }: { c: Ctx }) {
  const { data } = c
  const dark = useIsDark()

  const rows = data.machines.map((m) => {
    const byDate = new Map(m.days.map((d) => [d.date, d]))
    const changed = m.counts.length > 1
    const who = `U${m.unit} · ${m.name}`
    // The count on the last day with an entry, so the day the count changed
    // is marked and not every day after it.
    let prev: string | null = null
    return {
      key: m.code,
      label: m.full_name,
      cells: data.dates.map((date) => {
        const d = byDate.get(date)
        if (!d) return { eff: null, title: `${m.full_name} · ${date} · no entry` }
        const sw = changed && prev !== null && !!d.count && d.count !== prev
        if (d.count) prev = d.count
        return {
          eff: d.eff,
          edge: sw ? 'inset 3px 0 0 var(--color-text)' : undefined,
          title: `${sw ? `Count changed to ${d.count} · ` : ''}${who} · ${d.count || 'count not entered'} · ${date} · ${num(d.eff, 1)}% · ${num(d.actual)} of ${num(d.standard)} kg`,
        }
      }),
    }
  })

  // Coloured by the stage's place in the whole plant, not in the selection,
  // so a stage keeps its colour when the filter narrows to it.
  const series = data.stage_lines.map((s) => {
    const k = Math.max(0, data.stages.indexOf(s.stage))
    return { label: s.stage, color: stageColor(k), dash: stageDash(k), values: s.values }
  })

  return (
    <>
      <Blueprint className="sheet">
        <PanelHead title="Machine × day efficiency"
          right={<HeatLegend bench={c.bench} dark={dark} />} />
        <MachineHeat days={c.days} rows={rows} bench={c.bench} dark={dark} onOpen={c.open} />
        <div className="pretty" style={{
          fontSize: 12, color: 'var(--color-neutral-700)', marginTop: 14, lineHeight: 1.5,
        }}>
          A dark line inside a row marks the day that machine changed count (label reads
          20 KW → 30 KW); hover a cell for that day’s count. Rows run in machine order,
          unit by unit and stage by stage. A horizontal band of weak colour is a machine
          problem; a vertical band is a plant-wide day — power, cotton mix or manning.
        </div>
      </Blueprint>

      <Blueprint className="sheet">
        <PanelHead title="Stage efficiency, day by day" />
        <MachineDayLines days={c.days} series={series} height={260}
          bench={c.bench} benchLabel={c.benchLabel}
          title={(i) => `${dayNum(data.dates[i])} ${c.monLong}`} />
      </Blueprint>
    </>
  )
}
